import type { IndustryContent } from '@/types/content';

export const electricalContent: IndustryContent = {
  slug: 'electrical',
  name: 'Electrical',
  headline: 'The Operating System for Electrical Contractors',
  subheadline:
    'Manage permits, track certifications, and dispatch the right electrician to every job—all from one platform built for electrical pros.',
  heroImageUrl: '/images/industries/electrical-hero.webp',
  painPoints: [
    {
      title: 'Permit tracking is a nightmare',
      description:
        'Keeping track of open permits, inspections, and deadlines across multiple jobs leads to delays and compliance headaches.',
      icon: 'permit',
    },
    {
      title: 'Certifications expire unnoticed',
      description:
        "Technician licenses and certifications slip through the cracks. You find out they're expired at the worst possible time.",
      icon: 'certificate',
    },
    {
      title: 'Job costing is guesswork',
      description:
        "You don't know if a job was profitable until weeks later—if at all. Material costs and labor hours aren't tracked in real-time.",
      icon: 'calculator',
    },
    {
      title: 'Load calculations live on paper',
      description:
        'Panel schedules and load calculations are scattered across notebooks, making it hard to reference past work or train new techs.',
      icon: 'clipboard',
    },
    {
      title: 'Commercial bids take forever',
      description:
        'Creating detailed commercial estimates with labor units and material takeoffs is tedious and error-prone.',
      icon: 'estimate',
    },
  ],
  features: [
    {
      title: 'Smart Dispatch Board',
      description:
        'Match electricians by certification level, specialization, and location. Journeymen, masters, and apprentices—dispatched intelligently.',
      icon: 'dispatch',
    },
    {
      title: 'Permit & Inspection Tracking',
      description:
        'Track permits from application to final inspection. Automated reminders ensure nothing falls through the cracks.',
      icon: 'permit',
    },
    {
      title: 'Certification Management',
      description:
        'Track licenses, certifications, and training for every technician. Get alerts before anything expires.',
      icon: 'certificate',
    },
    {
      title: 'Material & Inventory Tracking',
      description:
        'Track wire, breakers, panels, and supplies in your warehouse and trucks. Know what you have and where it is.',
      icon: 'inventory',
    },
    {
      title: 'Job Profitability Dashboard',
      description:
        'See real-time labor hours, material costs, and margins for every job. Know if you are making money before the invoice goes out.',
      icon: 'profitability',
    },
    {
      title: 'Mobile-First Experience',
      description:
        'Techs capture photos of panels, create as-builts, and collect signatures on-site. Works offline in basements and mechanical rooms.',
      icon: 'mobile',
    },
  ],
  testimonial: {
    id: 'electrical-1',
    quote:
      'The Business Rhythm tools are a game-changer. Our weekly meetings actually work now because everything we need is in one place. Goals, scorecards, issues—all connected to our daily operations.',
    author: 'David Martinez',
    role: 'CEO',
    company: 'Spark Electric',
    industry: 'electrical',
    stats: {
      label: 'Meeting Efficiency',
      value: '2x',
    },
  },
  stats: [
    { label: 'Average Admin Time Saved', value: '45%' },
    { label: 'Permit Compliance Rate', value: '99%' },
    { label: 'Faster Estimate Creation', value: '3x' },
    { label: 'Job Profitability Visibility', value: '100%' },
  ],
  seo: {
    title: 'Electrical Contractor Software | Dispatch & Management | Omniforge',
    description:
      'Electrical contractor software built for pros. Smart dispatch, permit tracking, certification management, and mobile app. Start your free trial.',
    keywords: [
      'electrical contractor software',
      'electrician scheduling software',
      'electrical dispatch software',
      'electrical business software',
      'electrical service software',
    ],
  },
};
